// Update banner — visible only in the Android wrapper when the
// server has a tagged release newer than the installed versionName.
// Browser PWA renders nothing (SW handles their bundle updates).
//
// Uses the shared useUpdateCheck hook so the Settings → "Check for
// updates" button drives the same state and can cause the banner to
// re-appear after a manual recheck.

import { useState } from "preact/hooks";
import { Icon } from "../icons";
import { useUpdateCheck } from "../lib/use-update-check";

export function UpdateBanner({ notify }: { notify: (msg: string) => void }) {
  const update = useUpdateCheck();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!update.available || dismissed) return null;

  const onInstall = () => {
    setBusy(true);
    const result = update.install();
    if (result === "ok") {
      notify(`Downloading ${update.latestVersion}…`);
      setDismissed(true);
    } else if (result === "error:permission_needed") {
      notify("Grant 'Install unknown apps' in settings, then tap Install again.");
      setBusy(false);
    } else if (result === "no_bridge" || result === "no_update") {
      // Shouldn't reach — banner is gated on update.available && wrapper.
      setBusy(false);
    } else {
      notify(`Install failed: ${result.replace(/^error:/, "")}`);
      setBusy(false);
    }
  };

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <Icon name="download" size={14} />
      <span className="update-banner-text">
        Update available · <strong>v{update.latestVersion}</strong>
      </span>
      <button
        className="update-banner-btn"
        type="button"
        onClick={onInstall}
        disabled={busy}
      >
        {busy ? "Downloading…" : "Install"}
      </button>
      <button
        className="update-banner-x"
        type="button"
        onClick={() => setDismissed(true)}
        title="Dismiss"
        aria-label="Dismiss"
      >
        <Icon name="x" size={12} />
      </button>
    </div>
  );
}
